import { SystemEntity } from '@erp/shared-types';
import { Badge } from '@/components/ui/badge';
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';
import { CurrencyDollarIcon, ArrowUUpLeftIcon } from '@phosphor-icons/react';
import { SystemEntityPreview } from '../preview-ui';
import type { Charge } from '@erp/rpc';

/** Fields from Charge used for inline display */
type ChargeInlineData = Pick<
  Charge,
  | 'amount'
  | 'currencyCode'
  | 'chargeCategory'
  | 'direction'
  | 'isEstimate'
  | 'reversesChargeId'
>;

interface ChargeInlineProps {
  entityId: string;
  className?: string;
  // Optional: Pass charge data directly to avoid extra fetching
  chargeData?: ChargeInlineData;
}

function formatCurrency(amount: string, currencyCode: string): string {
  const numeric = Number(amount);
  if (Number.isNaN(numeric)) {
    return amount;
  }

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  });
}

export function ChargeInline({
  entityId,
  className,
  chargeData,
}: ChargeInlineProps) {
  const isReversal = !!chargeData?.reversesChargeId;

  const displayName = chargeData
    ? `${chargeData.chargeCategory?.label || chargeData.chargeCategory?.value || 'Charge'} ${formatCurrency(chargeData.amount, chargeData.currencyCode)}`
    : 'Charge';

  const badgeVariant = chargeData?.isEstimate ? 'secondary' : 'outline';

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div className="inline-block max-w-full">
          <Badge
            variant={badgeVariant}
            className={cn(
              'gap-1.5 cursor-pointer max-w-full',
              isReversal && 'border-amber-500 text-amber-700 bg-amber-50',
              className,
            )}
          >
            {isReversal ? (
              <ArrowUUpLeftIcon className="size-3 shrink-0" />
            ) : (
              <CurrencyDollarIcon className="size-3 shrink-0" />
            )}
            <span className="truncate">{displayName}</span>
          </Badge>
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-auto p-0" align="start">
        <SystemEntityPreview
          entityType={SystemEntity.CHARGE}
          entityId={entityId}
        />
      </HoverCardContent>
    </HoverCard>
  );
}
